import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ApiToken {
  id: string;
  name: string;
  lastUsedAt: string | null;
  createdAt: string;
}

/**
 * T147 (spec 001 Phase 10) — Personal API tokens UI.
 *
 * Backend (T042) already exists at /api/v1/api-tokens. Token plaintext is
 * returned ONCE on POST (`of_pat_<random>`) — the row never re-exposes it.
 * UI mirrors the deploy-hooks page: accordion create form, copy-once
 * reveal, list with delete.
 */
export function AccountTokens() {
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState<{ id: string; token: string } | null>(null);

  const list = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => api.get<{ tokens: ApiToken[] }>(`/api-tokens`).then((r) => r.tokens),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api-tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
  });

  return (
    <div className="pt-6 space-y-4">
      <div>
        <h1 className="text-lg font-semibold inline-flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" /> API tokens
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Personal access tokens authenticate the CLI and REST API as you. Tokens are
          shown once on creation; treat them like passwords.
        </p>
      </div>

      <CreateTokenForm
        onCreated={(id, token) => {
          setRevealed({ id, token });
          qc.invalidateQueries({ queryKey: ["api-tokens"] });
        }}
      />

      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold">Active tokens</h3>
          <span className="text-2xs text-subtle-foreground">{list.data?.length ?? 0}</span>
        </div>
        {list.isLoading && <Empty>Loading…</Empty>}
        {!list.isLoading && (list.data?.length ?? 0) === 0 && (
          <Empty>No tokens yet. Create one above to use the CLI.</Empty>
        )}
        {list.data?.map((t) => (
          <div key={t.id} className="of-row gap-3 items-start">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold truncate">{t.name}</span>
              </div>
              <div className="text-2xs text-subtle-foreground mt-0.5">
                Created {new Date(t.createdAt).toLocaleDateString()}
                {t.lastUsedAt ? <> · Last used {relative(t.lastUsedAt)}</> : <> · Never used</>}
              </div>
              {revealed?.id === t.id && (
                <CopyableToken token={revealed.token} onClose={() => setRevealed(null)} />
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-danger"
              onClick={() => {
                if (confirm(`Delete '${t.name}'? Any CLI session using it will stop working.`))
                  remove.mutate(t.id);
              }}
              disabled={remove.isPending}
              title="Delete token"
            >
              {remove.isPending && remove.variables === t.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateTokenForm({ onCreated }: { onCreated: (id: string, token: string) => void }) {
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; name: string; token: string }>(`/api-tokens`, { name }),
    onSuccess: (r) => {
      onCreated(r.id, r.token);
      setName("");
    },
  });
  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Create token
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        <div className="space-y-1">
          <Label htmlFor="token-name" className="text-xs">Name</Label>
          <Input
            id="token-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="laptop-cli"
            className="font-mono text-sm"
          />
          <div className="text-2xs text-subtle-foreground">A label to help you identify this token later.</div>
        </div>
        {create.error instanceof ApiError && (
          <div className="text-xs text-danger">{create.error.message}</div>
        )}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => create.mutate()}
            disabled={!name || create.isPending}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Create
          </Button>
        </div>
      </div>
    </details>
  );
}

function CopyableToken({ token, onClose }: { token: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-2 rounded-md border border-success/30 bg-success-subtle/50 p-3 space-y-2">
      <div className="text-2xs font-semibold">Token (shown ONCE — copy now)</div>
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
        <input
          readOnly
          value={token}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="w-full min-w-0 sm:w-auto sm:flex-1 h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <Button
          size="sm"
          variant="default"
          onClick={async () => {
            const ok = await copyText(token);
            if (ok) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Dismiss</Button>
      </div>
      <div className="text-2xs text-muted-foreground">
        Use as <span className="font-mono break-all">Authorization: Bearer {token.slice(0, 14)}…</span>
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
